import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  BASEMAP_ADDON_GIS,
  BASEMAP_ESRI_SATELLITE,
} from '@rsos/constants/basemapAddonNames';
import { ESRI } from '@rsos/maps/constants/mapProviders';
import { esriBasemapFromUiStringWithFallback } from '@rsos/maps/providers/esri/esriBasemapFromUiString';
import { createVectorTileLayer } from '@rsos/maps/providers/esri/layers/createVectorTileLayer';
import { selectBasemapService, selectGISServices } from '@rsos/reduxDataStore';
import { callQueueSelector } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import checkEsriMapAccess from '@rsos/utils/checkEsriMapAccess';
import checkGISServicesAccess from '@rsos/utils/checkGISServicesAccess';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import { captureExceptionWithScope } from '@rsos/utils/sentry';

const SATELLITE_REFERENCE_LAYER_ID = 'satellite-reference-layer';

const SATELLITE_REFERENCE_LAYER_NAME = 'satellite Reference Layer';

/**
 * Esri basemap transition handling
 * @param {Object} map - The Esri map oject.
 */
const useEsriMapTransition = map => {
  const mapInstance = map?.mapInstance;

  const hasEsriMapAccess = useSelector(state =>
    checkEsriMapAccess({
      psaps: state.psaps,
      sinatra: state.sinatra,
    }),
  );

  const hasGISServiceAccess = useSelector(state =>
    checkGISServicesAccess({
      psaps: state.psaps,
      sinatra: state.sinatra,
    }),
  );

  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);

  const basemap = useSelector(state => state.sinatra.ui.basemap);

  const mapAddons = useSelector(state => state.sinatra.ui.mapAddons);

  const isBasemapVisible = useSelector(
    state => state.sinatra.ui.isBasemapVisible,
  );

  // The GIS addon in the basemap menu refers to the Esri GIS cached-basemap service.
  const gisAddon = useSelector(selectBasemapService);
  const gisServices = useSelector(selectGISServices);
  const alternativeBasemapService = useMemo(() => {
    return gisServices.find(service => {
      return service.attributes?.gis_service_category === 'alternative_basemap';
    });
  }, [gisServices]);

  // Overlay the Esri satellite labels reference layer when the authority's
  // alternative basemap service has satellite labels enabled in Andromeda.
  // When there is no alternative basemap service, default to showing labels.
  const shouldShowSatelliteLabel = alternativeBasemapService
    ? Boolean(alternativeBasemapService.attributes?.satellite_labels)
    : true;

  const hasGISAddon = useMemo(() => {
    return hasGISServiceAccess && gisAddon;
  }, [hasGISServiceAccess, gisAddon]);

  const selectedCallerID = useSelector(
    state => state?.emergencyCalls?.selectedCallerID,
  );

  const selectedAlertID = useSelector(
    state => state?.incidents?.selectedAlertID,
  );

  const hasAniAliAccess = useSelector(state =>
    checkAniAliAccessSelector({ psaps: state.psaps, sinatra: state.sinatra }),
  );

  const callQueue = useSelector(state =>
    state?.emergencyCalls?.callQueue
      ? callQueueSelector(state.emergencyCalls.callQueue, hasAniAliAccess)
      : {},
  );

  const hasLocation = !!getMostRecentLoc(callQueue?.[selectedCallerID]);

  const isAllView = useMemo(() => {
    return (!selectedCallerID || !hasLocation) && !selectedAlertID;
  }, [hasLocation, selectedAlertID, selectedCallerID]);

  const findSatelliteReferenceLayer = useCallback(mapInstance => {
    if (mapInstance?.map) {
      return (
        mapInstance.map.findLayerById(SATELLITE_REFERENCE_LAYER_ID) || null
      );
    }

    return null;
  }, []);

  // Create and add the Esri satellite labels reference layer so the satellite
  // basemap can show labels. Only relevant when shouldShowSatelliteLabel.
  useEffect(() => {
    if (
      shouldShowSatelliteLabel &&
      basemap &&
      hasEsriMapAccess &&
      mapInstance?.view?.map?.basemap &&
      mapProvider === ESRI
    ) {
      mapInstance.view.map.basemap.when(async () => {
        const doesSatelliteReferenceLayerExist =
          !!findSatelliteReferenceLayer(mapInstance);

        if (!doesSatelliteReferenceLayerExist) {
          try {
            const satelliteReferenceLayer = await createVectorTileLayer({
              id: SATELLITE_REFERENCE_LAYER_ID,
              url: 'https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/styles/arcgis/imagery/labels',
              visible: basemap === BASEMAP_ESRI_SATELLITE && isBasemapVisible,
              title: SATELLITE_REFERENCE_LAYER_NAME,
            });

            mapInstance.map.add(satelliteReferenceLayer, 0);
          } catch (error) {
            captureExceptionWithScope(error, {
              extra: {
                errorMessage: error?.message,
              },
            });
          }
        }
      });
    }
  }, [
    basemap,
    findSatelliteReferenceLayer,
    hasEsriMapAccess,
    isBasemapVisible,
    mapInstance,
    mapProvider,
    shouldShowSatelliteLabel,
  ]);

  // Update the Esri basemap and visibility on the selection in the basemap menu.
  // If the Esri 'none' basemap is selected, the basemap visibility should be false.
  // Otherwise the basemap visibility should be true.
  useEffect(() => {
    let isEffectActive = true;

    if (
      basemap &&
      hasEsriMapAccess &&
      mapInstance?.view?.map?.basemap &&
      mapProvider === ESRI
    ) {
      mapInstance.view.map.basemap.when(async () => {
        const resolvedBasemap = await esriBasemapFromUiStringWithFallback(
          basemap,
          {
            hasAlternativeBasemap: Boolean(alternativeBasemapService),
            alternativeBasemapItemIds:
              alternativeBasemapService?.attributes?.map_id,
          },
        );

        if (!isEffectActive) {
          return;
        }

        mapInstance.view.map.basemap = resolvedBasemap;

        // Esri basemap (navigation or satellite imagery) layer.
        const basemapLayer = mapInstance.view.map.basemap.baseLayers.items[0];
        if (basemapLayer) basemapLayer.visible = isBasemapVisible;

        const satelliteReferenceLayer =
          findSatelliteReferenceLayer(mapInstance);

        if (satelliteReferenceLayer)
          satelliteReferenceLayer.visible =
            shouldShowSatelliteLabel &&
            basemap === BASEMAP_ESRI_SATELLITE &&
            isBasemapVisible;
      });
    }

    return () => {
      isEffectActive = false;
    };
  }, [
    basemap,
    findSatelliteReferenceLayer,
    hasEsriMapAccess,
    isBasemapVisible,
    mapInstance,
    mapProvider,
    alternativeBasemapService,
    alternativeBasemapService?.attributes?.map_id,
    shouldShowSatelliteLabel,
  ]);

  // Update the basemap menu addon (Esri GIS cached-basemap layer) visibility based on the selection
  // in the basemap menu.
  useEffect(() => {
    if (
      hasEsriMapAccess &&
      hasGISAddon &&
      mapAddons &&
      mapInstance?.map &&
      mapProvider === ESRI
    ) {
      const gisAddonLayer = mapInstance.map.findLayerById(gisAddon?.gisLayerID);

      if (gisAddonLayer)
        gisAddonLayer.visible = mapAddons.includes(BASEMAP_ADDON_GIS);
    }
  }, [
    gisAddon,
    hasEsriMapAccess,
    hasGISAddon,
    mapAddons,
    mapInstance,
    mapProvider,
  ]);

  // When the mapProvider is switched to Esri via the basemap menu,
  // update the map extent to the specific view state where the user was on Google map.
  useEffect(() => {
    if (
      mapInstance?.view &&
      mapProvider === ESRI &&
      window?.__RSOS?.mapViewState &&
      isAllView
    ) {
      const { extent } = window.__RSOS.mapViewState;

      mapInstance.view.extent = extent;

      delete window.__RSOS.mapViewState;
    }
  }, [isAllView, mapInstance, mapProvider]);
};

export default useEsriMapTransition;
